import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import { Toaster } from 'sonner'
import { XCircle } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useUser, useOrganization } from '@clerk/clerk-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NuqsAdapter } from 'nuqs/adapters/react-router'
import { AuthProvider } from './contexts/AuthProvider'
import { AgentSelection } from './components/AgentSelection'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ChameleonBridge } from '@/components/ChameleonBridge'
import { useVersionCheck } from './hooks/useVersionCheck'
import { AppShellEffects } from '@/components/layout/AppShellEffects'
import { NUQS_SHELL_DEFAULT_OPTIONS } from '@/lib/shellSearch'
import { useTabDrafts } from '@/stores/tabDrafts'
import {
  SelectedAgentProvider,
  useSelectedAgent,
} from './contexts/SelectedAgentContext'
import '@fontsource-variable/inter'
import './index.css'
import '@milkdown/crepe/theme/common/style.css'
import '@milkdown/crepe/theme/frame.css'

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  })
}

declare global {
  interface Window {
    Atlas?: { call?: (...args: any[]) => void }
  }
}

// Component to handle PostHog tracking within router context
function PostHogTracker() {
  const posthog = usePostHog()
  const { user } = useUser()

  if (user) {
    posthog?.identify(user.id, {
      email: user?.primaryEmailAddress?.emailAddress,
      name: user?.fullName,
      firstName: user?.firstName,
      lastName: user?.lastName,
    })

    // Also identify in Atlas if present
    try {
      window?.Atlas?.call?.('identify', {
        userId: user.id,
        name: user.fullName,
        email: user.primaryEmailAddress?.emailAddress,
      })
    } catch {
      // ignore if Atlas not loaded
    }
  }

  return null
}

function OrgProvider({ children }: { children: ReactNode }) {
  const { organization } = useOrganization()
  const [queryClient] = useState(() => createQueryClient())
  const prevOrgRef = useRef(organization?.id)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const orgChanged =
      prevOrgRef.current && prevOrgRef.current !== organization?.id

    if (orgChanged) {
      // Treat org switch as a full app refresh. This avoids any flashing or
      // stale state (tabs, conversations, etc.) by letting the browser reload
      // the entire SPA from a clean root URL.
      queryClient.invalidateQueries()
      // Drop the previous org's unsaved work first — drafts and the composite
      // tabs' dirty flags alike: none of it survives the reload, so the unload
      // guard must not offer to rescue it.
      useTabDrafts.setState({
        drafts: new Map(),
        dirtyTabFlags: new Set(),
        closingTabIds: new Set(),
      })
      window.location.href = '/'
    }

    prevOrgRef.current = organization?.id
  }, [organization?.id, queryClient, navigate, location.pathname])

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}

function App() {
  return (
    <BrowserRouter>
      <NuqsAdapter defaultOptions={NUQS_SHELL_DEFAULT_OPTIONS}>
        <SelectedAgentProvider>
          <AppShellEffects />
          <AppWithAgent />
        </SelectedAgentProvider>
      </NuqsAdapter>
    </BrowserRouter>
  )
}

function AppWithAgent() {
  useVersionCheck({ enabled: true })
  const { selectedAgentId, setSelectedAgentId } = useSelectedAgent()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Keep selected agent in sync with the URL so that deep links using either
  // path segments (/david, /hanna) or an ?agent= query parameter are the
  // single source of truth for which agent is active.
  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const root = segments[0]
    const agentFromPath =
      root === 'david' || root === 'hanna' ? (root as 'david' | 'hanna') : null
    const agentFromQuery = searchParams.get('agent')

    const nextAgent =
      (agentFromQuery === 'david' || agentFromQuery === 'hanna'
        ? agentFromQuery
        : null) ||
      agentFromPath ||
      selectedAgentId

    if (nextAgent !== selectedAgentId) {
      setSelectedAgentId(nextAgent)
    }
  }, [location.pathname, searchParams, selectedAgentId, setSelectedAgentId])

  return (
    <ErrorBoundary>
      <AuthProvider>
        <OrgProvider>
          <PostHogTracker />
          <ChameleonBridge />
          <Routes>
            {/* Keep the agent app mounted across main-panel route changes so in-progress
               streaming conversations don't reset when switching tabs. */}
            <Route path="/*" element={<AgentSelection />} />
          </Routes>
        </OrgProvider>
      </AuthProvider>
      <Toaster
        position="bottom-right"
        icons={{
          error: <XCircle className="h-5 w-5 text-red-500" />,
        }}
      />
    </ErrorBoundary>
  )
}

export default App
